import { useUser } from '@berky/auth'
import {
  Button,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  ErrorState,
  formatDateTime,
  FormField,
  LoadingSkeleton,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  toast,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@berky/ui'
import {
  Copy,
  ExternalLink,
  MoreHorizontal,
  Pencil,
  RotateCcw,
  Trash2,
  X,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useLocation } from 'react-router'

import {
  useFeedbackAlsGelesenMarkieren,
  useFeedback,
  useFeedbackAktivitaeten,
  useFeedbackBereichAendern,
  useFeedbackLoeschen,
  useFeedbackScreenshot,
  useFeedbackStatusAendern,
} from '../api'
import { FeedbackConversationComposer } from '../components/feedback-activity-forms'
import { FeedbackHistory } from '../components/feedback-history'
import { FeedbackStatusBadge } from '../components/feedback-status'
import { moduleReferences } from '../../../modules'
import { FeedbackAttachmentView } from '../../feedback-attachment'

const PLATTFORM_BEREICH = '__plattform__'

export function FeedbackDetailPage({ management = true }: { management?: boolean }) {
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const currentUser = useUser()
  const availableModules = moduleReferences
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [resetStatusDialogOpen, setResetStatusDialogOpen] = useState(false)
  const [bereichDialogOpen, setBereichDialogOpen] = useState(false)
  const [ausgewaehlterBereich, setAusgewaehlterBereich] = useState('')
  const query = useFeedback(id)
  const screenshotQuery = useFeedbackScreenshot(id, query.data?.art === 'Seitenfeedback')
  const activitiesQuery = useFeedbackAktivitaeten(id)
  const bereichMutation = useFeedbackBereichAendern(id)
  const statusMutation = useFeedbackStatusAendern(id)
  const deleteMutation = useFeedbackLoeschen(id)
  const readMutation = useFeedbackAlsGelesenMarkieren(id)
  const lastActivityMarker = activitiesQuery.data?.at(-1)?.id ?? query.data?.angelegtAm
  const markAsRead = readMutation.mutate
  const lastMarkedActivity = useRef<string | undefined>(undefined)
  const location = useLocation()
  const listPath = management ? '/feedback' : `/mein-feedback${location.search}`

  useEffect(() => {
    if (!query.isSuccess || !activitiesQuery.isSuccess || !lastActivityMarker) return

    const marker = `${id}:${lastActivityMarker}`
    if (lastMarkedActivity.current === marker) return
    lastMarkedActivity.current = marker
    markAsRead()
  }, [activitiesQuery.isSuccess, id, lastActivityMarker, markAsRead, query.isSuccess])

  if (query.isPending) return <LoadingSkeleton variant="form" rows={7} />
  if (query.isError) {
    return (
      <ErrorState
        description="Das Feedback konnte nicht geladen werden."
        details={query.error.message}
        onRetry={() => void query.refetch()}
      />
    )
  }

  const entry = query.data
  const istAllgemeineIdee = entry.art === 'AllgemeineIdee'
  const external = entry.art === 'ExternesFeedback'
  const screenshot = screenshotQuery.data
  const umgebung = ermittleUmgebung()
  const screenshotGroesse =
    screenshot && screenshot.breite > 1 && screenshot.hoehe > 1
      ? `${screenshot.breite} × ${screenshot.hoehe} px`
      : 'Nicht verfügbar'
  const aktuellerBereich = entry.modulId ?? PLATTFORM_BEREICH
  const bereiche = [
    { modulId: null, bezeichnung: 'Plattform' },
    ...availableModules.map((module) => ({
      modulId: module.id,
      bezeichnung: module.title,
    })),
  ].toSorted((a, b) => {
    return a.bezeichnung.localeCompare(b.bezeichnung, 'de')
  })
  const aktuelleBereichsbezeichnung = istAllgemeineIdee
    ? 'Allgemeine Idee'
    : external
      ? (entry.kategorieName ?? entry.seitentitel)
      : (bereiche.find(
          (bereich) => (bereich.modulId ?? PLATTFORM_BEREICH) === aktuellerBereich,
        )?.bezeichnung ??
        entry.modulId ??
        'Plattform')

  return (
    <>
      <article aria-labelledby="feedback-detail-titel">
        <div className="mb-3 flex items-start gap-2">
          <div className="flex min-h-9 min-w-0 flex-1 flex-wrap content-center items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
            <span className="font-medium tabular-nums">{entry.reference}</span>
            <span aria-hidden="true">·</span>
            <FeedbackStatusBadge
              status={entry.status}
              zurueckgestelltBis={entry.zurueckgestelltBis}
            />
            <span aria-hidden="true">·</span>
            <span>{aktuelleBereichsbezeichnung}</span>
            <span aria-hidden="true">·</span>
            <span>{formatDateTime(entry.angelegtAm)}</span>
            <span aria-hidden="true">·</span>
            <span>Ersteller: {entry.erstellerName}</span>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {management && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label="Weitere Aktionen">
                    <MoreHorizontal aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {entry.art === 'Seitenfeedback' && (
                    <DropdownMenuItem
                      onSelect={() => {
                        setAusgewaehlterBereich(aktuellerBereich)
                        setBereichDialogOpen(true)
                      }}
                    >
                      <Pencil aria-hidden="true" />
                      Bereich ändern
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    disabled={entry.status === 'Neu'}
                    onSelect={() => setResetStatusDialogOpen(true)}
                  >
                    <RotateCcw aria-hidden="true" />
                    Status auf Neu zurücksetzen
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    variant="destructive"
                    onSelect={() => setDeleteDialogOpen(true)}
                  >
                    <Trash2 aria-hidden="true" />
                    Feedback löschen
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" asChild>
                  <Link to={listPath} aria-label="Schließen und zurück zur Liste">
                    <X aria-hidden="true" />
                  </Link>
                </Button>
              </TooltipTrigger>
              <TooltipContent>Schließen</TooltipContent>
            </Tooltip>
          </div>
        </div>

        <div className="border-b pb-4">
          <h2
            id="feedback-detail-titel"
            className="whitespace-pre-wrap text-xl font-semibold leading-snug"
          >
            {entry.text}
          </h2>
        </div>

        <div className="mt-5 grid gap-5 2xl:grid-cols-2">
          <section aria-labelledby="screenshot">
            <h3 id="screenshot" className="mb-3 text-base font-semibold">
              {external
                ? 'Dateianhang'
                : istAllgemeineIdee
                  ? 'Allgemeine Idee'
                  : 'Markierter Screenshot'}
            </h3>
            {external ? (
              <FeedbackAttachmentView
                feedbackId={entry.id}
                hasAttachment={!!entry.hatAnhang}
              />
            ) : istAllgemeineIdee ? (
              <div className="rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground">
                Diese Idee wurde ohne Screenshot und ohne Bezug zu einem Modul
                eingereicht.
              </div>
            ) : (
              <>
                {screenshotQuery.isPending && <LoadingSkeleton variant="form" rows={3} />}
                {screenshotQuery.isError && (
                  <ErrorState
                    description="Der Screenshot konnte nicht geladen werden."
                    details={screenshotQuery.error.message}
                    onRetry={() => void screenshotQuery.refetch()}
                  />
                )}
                {screenshot && (
                  <div className="overflow-auto rounded-lg border bg-card p-2">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button
                          variant="ghost"
                          className="h-auto w-full cursor-zoom-in p-0 hover:bg-transparent"
                          aria-label="Markierten Screenshot vergrößern"
                        >
                          <img
                            src={`data:${screenshot.contentType};base64,${screenshot.datenBase64}`}
                            width={screenshot.breite}
                            height={screenshot.hoehe}
                            alt={`Markierter Screenshot zum Feedback: ${entry.text}`}
                            className="h-auto max-w-full"
                          />
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-h-[90svh] max-w-[95vw] overflow-auto p-2 sm:max-w-6xl">
                        <DialogHeader>
                          <DialogTitle className="sr-only">
                            Markierten Screenshot vergrößert anzeigen
                          </DialogTitle>
                        </DialogHeader>
                        <img
                          src={`data:${screenshot.contentType};base64,${screenshot.datenBase64}`}
                          width={screenshot.breite}
                          height={screenshot.hoehe}
                          alt={`Vergrößerter markierter Screenshot zum Feedback: ${entry.text}`}
                          className="max-h-[85svh] w-auto max-w-full object-contain"
                        />
                      </DialogContent>
                    </Dialog>
                  </div>
                )}

                <details className="mt-3 rounded-lg border bg-card">
                  <summary className="cursor-pointer px-4 py-3 text-sm font-medium">
                    Kontext und technische Details
                  </summary>
                  <div className="border-t text-sm">
                    <section className="space-y-3 px-4 py-3" aria-labelledby="herkunft">
                      <h4 id="herkunft" className="text-sm font-semibold">
                        Herkunft
                      </h4>
                      <dl className="grid gap-3 sm:grid-cols-2">
                        <Feld label="Ersteller">
                          {entry.erstellerName}
                          <span className="block text-muted-foreground">
                            {entry.erstellerEmail}
                          </span>
                        </Feld>
                        <Feld label="Seite">{entry.seitentitel}</Feld>
                        <div className="sm:col-span-2">
                          <Feld label="Pfad">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="min-w-0 flex-1 break-all" data-numeric>
                                {entry.seitenPfad}
                              </span>
                              <div className="flex shrink-0 items-center gap-1.5">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 px-2 text-xs"
                                  asChild
                                >
                                  <a
                                    href={entry.seitenPfad}
                                    target="_blank"
                                    rel="noreferrer"
                                    aria-label={`Seite ${entry.seitenPfad} in neuem Tab öffnen`}
                                  >
                                    <ExternalLink aria-hidden="true" />
                                    Öffnen
                                  </a>
                                </Button>
                                <KopierenButton
                                  wert={entry.seitenPfad}
                                  bezeichnung="Pfad"
                                />
                              </div>
                            </div>
                          </Feld>
                        </div>
                      </dl>
                    </section>

                    <Separator />

                    <section
                      className="space-y-3 px-4 py-3"
                      aria-labelledby="technische-angaben"
                    >
                      <h4 id="technische-angaben" className="text-sm font-semibold">
                        Technische Angaben
                      </h4>
                      <dl className="grid gap-3 sm:grid-cols-2">
                        <Feld label="Umgebung">{umgebung}</Feld>
                        <Feld label="Screenshot-Größe">{screenshotGroesse}</Feld>
                        <div className="sm:col-span-2">
                          <Feld label="Build-ID">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="min-w-0 flex-1 break-all" data-numeric>
                                {entry.buildId}
                              </span>
                              <KopierenButton
                                wert={entry.buildId}
                                bezeichnung="Build-ID"
                              />
                            </div>
                          </Feld>
                        </div>
                      </dl>
                    </section>
                  </div>
                </details>
              </>
            )}
          </section>

          <section aria-labelledby="gespraech">
            <h3 id="gespraech" className="mb-3 text-base font-semibold">
              Gespräch
            </h3>
            <div className="overflow-hidden rounded-lg border bg-card">
              <div className="max-h-96 overflow-y-auto bg-muted/30 p-4">
                {activitiesQuery.isPending && <LoadingSkeleton variant="text" rows={4} />}
                {activitiesQuery.isError && (
                  <ErrorState
                    description="Das Gespräch konnte nicht geladen werden."
                    details={activitiesQuery.error.message}
                    onRetry={() => void activitiesQuery.refetch()}
                  />
                )}
                {activitiesQuery.data && (
                  <FeedbackHistory
                    entry={entry}
                    activities={activitiesQuery.data}
                    currentUserId={currentUser.id}
                  />
                )}
              </div>
              <Separator />
              <FeedbackConversationComposer
                feedbackId={id}
                modulId={entry.modulId}
                status={entry.status}
                zurueckgestelltBis={entry.zurueckgestelltBis}
                managementActions={management}
              />
            </div>
          </section>
        </div>
      </article>

      <Dialog
        open={bereichDialogOpen}
        onOpenChange={(open) => {
          setBereichDialogOpen(open)
          if (open) setAusgewaehlterBereich(aktuellerBereich)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bereich ändern</DialogTitle>
            <DialogDescription>
              Aktueller Bereich: {aktuelleBereichsbezeichnung}
            </DialogDescription>
          </DialogHeader>

          <FormField label="Neuer Bereich" htmlFor="feedback-bereich">
            <Select value={ausgewaehlterBereich} onValueChange={setAusgewaehlterBereich}>
              <SelectTrigger id="feedback-bereich" className="w-full">
                <SelectValue placeholder="Bereich auswählen" />
              </SelectTrigger>
              <SelectContent>
                {bereiche.map((bereich) => (
                  <SelectItem
                    key={bereich.modulId ?? PLATTFORM_BEREICH}
                    value={bereich.modulId ?? PLATTFORM_BEREICH}
                  >
                    {bereich.bezeichnung}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBereichDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button
              disabled={
                bereichMutation.isPending ||
                ausgewaehlterBereich === '' ||
                ausgewaehlterBereich === aktuellerBereich
              }
              onClick={() =>
                bereichMutation.mutate(
                  ausgewaehlterBereich === PLATTFORM_BEREICH
                    ? null
                    : ausgewaehlterBereich,
                  {
                    onSuccess: () => {
                      setBereichDialogOpen(false)
                      toast.success('Bereich geändert')
                    },
                    onError: (error) =>
                      toast.error('Bereich konnte nicht geändert werden', {
                        description: error.message,
                      }),
                  },
                )
              }
            >
              Bereich speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={resetStatusDialogOpen}
        onOpenChange={setResetStatusDialogOpen}
        title="Status auf Neu zurücksetzen?"
        description="Der aktuelle Bearbeitungsstatus wird zurückgesetzt. Im Gespräch erscheint dazu eine Statusmeldung."
        confirmLabel="Status zurücksetzen"
        pending={statusMutation.isPending}
        onConfirm={() =>
          statusMutation.mutate('Neu', {
            onSuccess: () => {
              setResetStatusDialogOpen(false)
              toast.success('Status auf Neu zurückgesetzt')
            },
            onError: (error) =>
              toast.error('Status konnte nicht zurückgesetzt werden', {
                description: error.message,
              }),
          })
        }
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Feedback wirklich löschen?"
        description="Das Feedback einschließlich Screenshot und Verlauf wird dauerhaft entfernt. Diese Aktion kann nicht rückgängig gemacht werden."
        confirmLabel="Feedback löschen"
        destructive
        pending={deleteMutation.isPending}
        onConfirm={() =>
          deleteMutation.mutate(undefined, {
            onSuccess: () => {
              setDeleteDialogOpen(false)
              toast.success('Feedback gelöscht')
              void navigate('/feedback')
            },
            onError: (error) =>
              toast.error('Feedback konnte nicht gelöscht werden', {
                description: error.message,
              }),
          })
        }
      />
    </>
  )
}

function Feld({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="mt-1">{children}</dd>
    </div>
  )
}

function KopierenButton({ wert, bezeichnung }: { wert: string; bezeichnung: string }) {
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="h-7 shrink-0 px-2 text-xs"
      onClick={() => void inZwischenablageKopieren(wert, bezeichnung)}
      aria-label={`${bezeichnung} kopieren`}
    >
      <Copy aria-hidden="true" />
      Kopieren
    </Button>
  )
}

async function inZwischenablageKopieren(wert: string, bezeichnung: string) {
  try {
    if (!navigator.clipboard?.writeText) throw new Error('Clipboard nicht verfügbar')
    await navigator.clipboard.writeText(wert)
    toast.success(`${bezeichnung} kopiert`)
  } catch {
    toast.error(`${bezeichnung} konnte nicht kopiert werden`)
  }
}

function ermittleUmgebung() {
  if (typeof window === 'undefined') return 'Nicht verfügbar'

  const hostname = window.location.hostname.toLocaleLowerCase('de')
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'Lokal'
  if (hostname === 'portal-test.berky.de') return 'Testsystem'
  if (hostname === 'portal.berky.de') return 'Echtsystem'
  return hostname || 'Nicht verfügbar'
}
